'use client';

import { motion } from 'motion/react';
import Link from 'next/link';
import { useMemo, useRef } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia } from '@/components/VideoMedia';
import { useLocale } from '@/contexts/LocaleContext';
import type { Blog, Media, MediaBlock } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import styles from './BlogCard.module.scss';

type BlogCardProps = Blog & {
  direction?: 'up' | 'down';
  className?: string;
  disableParallax?: boolean;
  tracking?: {
    type: string;
    headline: string;
    cta: string;
  };
  isThreeInARow?: boolean;
  overrideAspectRatio?: NonNullable<
    MediaBlock['medias']
  >[number]['aspectRatio'];
};

export const BlogCard: React.FC<BlogCardProps> = (props) => {
  const {
    card,
    className,
    hero,
    overrideAspectRatio,
    publishedAt,
    readingTime,
    title,
    tracking,
    url,
  } = props;
  const locale = useLocale();
  const t = getDictionary(locale);

  const ref = useRef<HTMLAnchorElement>(null);

  const MotionLink = useMemo(() => motion.create(Link), []);

  const {
    image: heroImage,
    mediaType: heroMediaType,
    videoEmbedUrl: heroVideoEmbedUrl,
    videoPoster: heroVideoPoster,
  } = hero;

  const {
    image: cardImage,
    mediaType: cardMediaType,
    override,
    title: cardTitle,
    videoEmbedUrl: cardVideoEmbedUrl,
    videoPoster: cardVideoPoster,
  } = card;

  const titleToUse = (override && cardTitle) || title;

  const MediaComponent =
    override && cardMediaType === 'image' && cardImage ? (
      <ImageMedia
        aspectRatio={overrideAspectRatio || 'standard'}
        image={cardImage}
        className={styles.image}
        responsiveWidths={
          overrideAspectRatio === 'square'
            ? {
                mobile: 343,
                desktop: 512,
                largeDesktop: 2256,
              }
            : overrideAspectRatio === 'portrait'
              ? {
                  mobile: 343,
                  desktop: 512,
                  largeDesktop: 800,
                }
              : {
                  mobile: 343,
                  desktop: 480,
                  largeDesktop: 908,
                }
        }
      />
    ) : override && cardMediaType === 'video' && cardVideoEmbedUrl ? (
      <VideoMedia
        aspectRatio={overrideAspectRatio || 'standard'}
        videoPoster={cardVideoPoster as Media}
        videoEmbedUrl={cardVideoEmbedUrl}
        videoIsBackground={true}
        videoLoops={true}
        className={styles.image}
      />
    ) : heroMediaType === 'image' && heroImage ? (
      <ImageMedia
        aspectRatio={overrideAspectRatio || 'standard'}
        image={heroImage}
        className={styles.image}
        responsiveWidths={
          overrideAspectRatio === 'square'
            ? {
                mobile: 343,
                desktop: 512,
                largeDesktop: 2256,
              }
            : overrideAspectRatio === 'portrait'
              ? {
                  mobile: 343,
                  desktop: 512,
                  largeDesktop: 800,
                }
              : {
                  mobile: 343,
                  desktop: 480,
                  largeDesktop: 908,
                }
        }
      />
    ) : heroMediaType === 'video' && heroVideoEmbedUrl ? (
      <VideoMedia
        aspectRatio={overrideAspectRatio || 'standard'}
        videoPoster={heroVideoPoster as Media}
        videoEmbedUrl={heroVideoEmbedUrl}
        videoIsBackground={true}
        videoLoops={true}
        className={styles.image}
      />
    ) : null;

  const handleClick = () => {
    if (tracking) {
      pushGTMEvent({
        event: 'component_click',
        component_cta: tracking.cta,
        component_headline: tracking.headline,
        component_type: tracking.type,
      });
    }
  };

  return (
    <MotionLink
      ref={ref}
      href={url as string}
      className={cn(styles.card, className)}
      onClick={handleClick}
    >
      {MediaComponent}
      <div className={styles.content}>
        {publishedAt && (
          <time className={styles.published}>
            {new Date(publishedAt).toLocaleDateString('da-DK', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
        )}
        {titleToUse && <span className={styles.title}>{titleToUse}</span>}
        {readingTime && (
          <span className={styles.readingTime}>
            {t('readingTime')} <strong>{readingTime} min.</strong>
          </span>
        )}
      </div>
    </MotionLink>
  );
};
